import * as React from 'react';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/shared/lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * Keep the typed case. Plain text fields are uppercased as you type
   * (BL-35 — business data is stored in capitals everywhere); set this on
   * technical values where case matters: SMTP/API credentials, URLs typed
   * into a text field, usernames, slugs.
   */
  preserveCase?: boolean;
}

const CASE_SENSITIVE_AUTOCOMPLETE = /email|username|password|url|one-time-code/;
const NON_TEXT_INPUT_MODES = new Set(['email', 'url', 'numeric', 'decimal', 'tel']);

function shouldUppercase(props: InputProps): boolean {
  if (props.preserveCase || props.readOnly) return false;
  const type = props.type ?? 'text';
  if (type !== 'text' && type !== 'search') return false;
  if (props.inputMode && NON_TEXT_INPUT_MODES.has(props.inputMode)) return false;
  if (props.autoComplete && CASE_SENSITIVE_AUTOCOMPLETE.test(props.autoComplete)) return false;
  return true;
}

// Uppercases in place and keeps the caret where it was, then hands the same
// event on — works for controlled inputs and react-hook-form `register`.
function uppercaseEvent(e: React.ChangeEvent<HTMLInputElement>): void {
  const input = e.target;
  const upper = input.value.toUpperCase();
  if (upper === input.value) return;
  const { selectionStart, selectionEnd } = input;
  input.value = upper;
  if (selectionStart !== null && selectionEnd !== null) input.setSelectionRange(selectionStart, selectionEnd);
}

// Shared anatomy for everything you type or pick into (DESIGN.md §5).
export const controlClass =
  'flex h-control w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive';

const baseInputClass = `${controlClass} file:border-0 file:bg-transparent file:text-sm file:font-medium`;

// Romanian keyboards type a decimal comma; the backend and zod want a dot.
function decimalCommaEvent(e: React.ChangeEvent<HTMLInputElement>): void {
  const input = e.target;
  if (!input.value.includes(',')) return;
  const { selectionStart, selectionEnd } = input;
  input.value = input.value.replace(/,/g, '.');
  if (selectionStart !== null && selectionEnd !== null) input.setSelectionRange(selectionStart, selectionEnd);
}

const InputBase = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, preserveCase, onChange, ...props }, ref) => {
    const isUppercase = shouldUppercase({ ...props, type, preserveCase });
    const isDecimal = props.inputMode === 'decimal';
    return (
      <input
        type={type}
        className={cn(baseInputClass, className)}
        ref={ref}
        onChange={
          onChange && (isUppercase || isDecimal)
            ? (e) => {
                if (isUppercase) uppercaseEvent(e);
                if (isDecimal) decimalCommaEvent(e);
                onChange(e);
              }
            : onChange
        }
        {...props}
      />
    );
  },
);
InputBase.displayName = 'InputBase';

// ---------------------------------------------------------------------------
// Romanian-locale date / datetime input.
//
// Native `<input type="date">` / `type="datetime-local"` always display in
// the browser/OS locale format — on most operators' machines that's
// mm/dd/yyyy, which reads wrong for a dd.mm.yyyy-speaking user and can't be
// fixed with CSS or a `lang` attribute (browsers ignore it for the
// closed-state text). So this renders its own always-dd.mm.yyyy[ HH:mm]
// text field for display/typing, backed by a visually-hidden native input
// that supplies the calendar-grid picker (via `showPicker()`) and native
// min/max validation. The value/onChange contract stays identical to a
// plain date/datetime-local input: ISO in, ISO out — so callers don't need
// to change.
// ---------------------------------------------------------------------------

function digitsToDmy(digits: string): string {
  if (digits.length > 4) return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4, 8)}`;
  if (digits.length > 2) return `${digits.slice(0, 2)}.${digits.slice(2, 4)}`;
  return digits;
}

function isValidDate(day: number, month: number, year: number): boolean {
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day;
}

function isoToDisplay(iso: string, withTime: boolean): string {
  if (withTime) {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
    return m ? `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}` : '';
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
}

function digitsToDisplay(digits: string, withTime: boolean): string {
  if (!withTime) return digitsToDmy(digits);
  const datePart = digitsToDmy(digits.slice(0, 8));
  const timeDigits = digits.slice(8, 12);
  if (timeDigits.length === 0) return datePart;
  const timePart = timeDigits.length > 2 ? `${timeDigits.slice(0, 2)}:${timeDigits.slice(2, 4)}` : timeDigits;
  return `${datePart} ${timePart}`;
}

/** Parses fully-typed digits (8 for date, 12 for datetime-local) into an ISO string, or null if invalid. */
function digitsToIso(digits: string, withTime: boolean): string | null {
  const expectedLength = withTime ? 12 : 8;
  if (digits.length !== expectedLength) return null;
  const day = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4, 8));
  if (!isValidDate(day, month, year)) return null;
  const isoDate = `${digits.slice(4, 8)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`;
  if (!withTime) return isoDate;
  const hour = Number(digits.slice(8, 10));
  const minute = Number(digits.slice(10, 12));
  if (hour > 23 || minute > 59) return null;
  return `${isoDate}T${digits.slice(8, 10)}:${digits.slice(10, 12)}`;
}

interface RoTemporalInputProps extends Omit<InputProps, 'type' | 'value' | 'onChange'> {
  type: 'date' | 'datetime-local';
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}

const RoTemporalInput = React.forwardRef<HTMLInputElement, RoTemporalInputProps>(
  ({ className, type, value, onChange, disabled, min, max, id, preserveCase: _preserveCase, ...props }, ref) => {
    const withTime = type === 'datetime-local';
    const isoValue = typeof value === 'string' ? value : '';
    const hiddenRef = React.useRef<HTMLInputElement>(null);
    const [text, setText] = React.useState(() => isoToDisplay(isoValue, withTime));
    const maxDigits = withTime ? 12 : 8;

    // Keep the visible text in sync when the value changes from outside
    // (e.g. programmatic reset, or a pick via the hidden native picker).
    React.useEffect(() => {
      setText(isoToDisplay(isoValue, withTime));
    }, [isoValue, withTime]);

    const emitIso = (iso: string): void => {
      if (!onChange) return;
      const event = {
        target: { value: iso },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(event);
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, maxDigits);
      setText(digitsToDisplay(digits, withTime));
      if (digits.length === 0) {
        emitIso('');
      } else if (digits.length === maxDigits) {
        const iso = digitsToIso(digits, withTime);
        if (iso) emitIso(iso);
      }
    };

    const handleBlur = (): void => {
      // Revert unfinished/invalid typing back to the last valid value.
      setText(isoToDisplay(isoValue, withTime));
    };

    const openPicker = (): void => {
      const el = hiddenRef.current;
      if (!el || disabled) return;
      if (typeof el.showPicker === 'function') {
        try {
          el.showPicker();
          return;
        } catch {
          // Falls through to .focus() below (showPicker() throws if not
          // triggered by direct user activation in some browsers).
        }
      }
      el.focus();
    };

    return (
      <div className="relative">
        <input
          id={id}
          type="text"
          inputMode="numeric"
          placeholder={withTime ? 'zz.ll.aaaa hh:mm' : 'zz.ll.aaaa'}
          value={text}
          onChange={handleTextChange}
          onBlur={handleBlur}
          disabled={disabled}
          className={cn(baseInputClass, 'pr-9', className)}
          {...props}
          ref={ref}
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          onClick={openPicker}
          aria-label="Deschide calendar"
          className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
        >
          <CalendarIcon className="h-4 w-4" />
        </button>
        <input
          ref={hiddenRef}
          type={type}
          tabIndex={-1}
          aria-hidden="true"
          disabled={disabled}
          value={isoValue}
          min={min as string | undefined}
          max={max as string | undefined}
          onChange={(e) => emitIso(e.target.value)}
          className="sr-only"
        />
      </div>
    );
  },
);
RoTemporalInput.displayName = 'RoTemporalInput';

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ type, value, ...props }, ref) => {
    if (type === 'date' || type === 'datetime-local') {
      return (
        <RoTemporalInput
          ref={ref}
          type={type as 'date' | 'datetime-local'}
          value={value as string | undefined}
          {...props}
        />
      );
    }
    return <InputBase type={type} value={value} ref={ref} {...props} />;
  },
);
Input.displayName = 'Input';
